import React, { useState } from 'react';
import { User, MapPin, Phone, Mail, CreditCard } from 'lucide-react';

/**
 * CustomerInfoForm component for collecting delivery address and customer information
 */
function CustomerInfoForm({ onInfoChange, customerInfo }) {
  const [formData, setFormData] = useState({
    shippingMethod: customerInfo?.shippingMethod || 'delivery',
    fullName: customerInfo?.fullName || '',
    email: customerInfo?.email || '',
    phone: customerInfo?.phone || '',
    address: customerInfo?.address || '',
    city: customerInfo?.city || '',
    postalCode: customerInfo?.postalCode || '',
    country: customerInfo?.country || 'Norge',
    specialInstructions: customerInfo?.specialInstructions || '',
    ...customerInfo
  });

  const [errors, setErrors] = useState({});

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = {
      ...formData,
      [name]: value
    };
    
    setFormData(updatedFormData);
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
    
    // Notify parent component
    onInfoChange(updatedFormData);
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Fullt navn er påkrevd';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'E-post er påkrevd';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Ugyldig e-postadresse';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Telefonnummer er påkrevd';
    } else if (!/^[\d\s\+\-\(\)]{8,}$/.test(formData.phone.replace(/\s/g, ''))) {
      newErrors.phone = 'Ugyldig telefonnummer';
    }
    
    if (formData.shippingMethod === 'delivery') {
      if (!formData.address.trim()) {
        newErrors.address = 'Adresse er påkrevd';
      }
      
      if (!formData.city.trim()) {
        newErrors.city = 'By er påkrevd';
      }
      
      if (!formData.postalCode.trim()) {
        newErrors.postalCode = 'Postnummer er påkrevd';
      } else if (!/^\d{4}$/.test(formData.postalCode)) {
        newErrors.postalCode = 'Postnummer må være 4 siffer';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isFormValid = () => {
    const baseValid = formData.fullName && formData.email && formData.phone && /\S+@\S+\.\S+/.test(formData.email);
    if (formData.shippingMethod === 'pickup') return baseValid;
    return baseValid && formData.address && formData.city && formData.postalCode && /^\d{4}$/.test(formData.postalCode);
  };

  return (
    <div className="customer-info-form">
      <div className="form-header">
        <h3>
          <User size={20} />
          Leveringsinformasjon
        </h3>
        <p>Fyll ut informasjonen for levering av dine varer</p>
      </div>

      <div className="form-sections">
        {/* Shipping Method */}
        <div className="form-section">
          <h4>Leveringsmåte</h4>
          <div className="form-group">
            <div className="shipping-options" role="radiogroup" aria-label="Leveringsmåte">
              <input
                className="shipping-radio"
                type="radio"
                id="shipping_delivery"
                name="shippingMethod"
                value="delivery"
                checked={formData.shippingMethod === 'delivery'}
                onChange={handleInputChange}
              />
              <label className="shipping-option" htmlFor="shipping_delivery">
                Levering til adresse
              </label>

              <input
                className="shipping-radio"
                type="radio"
                id="shipping_pickup"
                name="shippingMethod"
                value="pickup"
                checked={formData.shippingMethod === 'pickup'}
                onChange={handleInputChange}
              />
              <label className="shipping-option" htmlFor="shipping_pickup">
                Hent på lager (Bølerveien 24B)
              </label>
            </div>
          </div>
          {formData.shippingMethod === 'pickup' && (
            <div className="status-valid" style={{ marginTop: '0.5rem' }}>
              <span>Du har valgt henting på vårt lager: Bølerveien 24B</span>
            </div>
          )}
        </div>
        {/* Personal Information */}
        <div className="form-section">
          <h4>
            <User size={16} />
            Personlig informasjon
          </h4>
          
          <div className="form-group">
            <label htmlFor="fullName">
              Fullt navn *
            </label>
            <input
              type="text"
              id="fullName"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              className={errors.fullName ? 'error' : ''}
              placeholder="Ola Nordmann"
              required
            />
            {errors.fullName && <span className="error-message">{errors.fullName}</span>}
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="email">
                <Mail size={14} />
                E-post *
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={errors.email ? 'error' : ''}
                placeholder="ola@eksempel.no"
                required
              />
              {errors.email && <span className="error-message">{errors.email}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="phone">
                <Phone size={14} />
                Telefon *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                className={errors.phone ? 'error' : ''}
                placeholder="+47 123 45 678"
                required
              />
              {errors.phone && <span className="error-message">{errors.phone}</span>}
            </div>
          </div>
        </div>

        {/* Delivery Address */}
        {formData.shippingMethod === 'delivery' && (
          <div className="form-section">
            <h4>
              <MapPin size={16} />
              Leveringsadresse
            </h4>
            
            <div className="form-group">
              <label htmlFor="address">
                Gateadresse *
              </label>
              <input
                type="text"
                id="address"
                name="address"
                value={formData.address}
                onChange={handleInputChange}
                className={errors.address ? 'error' : ''}
                placeholder="Storgata 1"
                required
              />
              {errors.address && <span className="error-message">{errors.address}</span>}
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="postalCode">
                  Postnummer *
                </label>
                <input
                  type="text"
                  id="postalCode"
                  name="postalCode"
                  value={formData.postalCode}
                  onChange={handleInputChange}
                  className={errors.postalCode ? 'error' : ''}
                  placeholder="0150"
                  maxLength={4}
                  required
                />
                {errors.postalCode && <span className="error-message">{errors.postalCode}</span>}
              </div>

              <div className="form-group">
                <label htmlFor="city">
                  By *
                </label>
                <input
                  type="text"
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  className={errors.city ? 'error' : ''}
                  placeholder="Oslo"
                  required
                />
                {errors.city && <span className="error-message">{errors.city}</span>}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="country">
                Land
              </label>
              <select
                id="country"
                name="country"
                value={formData.country}
                onChange={handleInputChange}
              >
                <option value="Norge">Norge</option>
                <option value="Sverige">Sverige</option>
                <option value="Danmark">Danmark</option>
                <option value="Finland">Finland</option>
              </select>
            </div>
          </div>
        )}

        {/* Special Instructions */}
        <div className="form-section">
          <h4>Spesielle instruksjoner</h4>
          
          <div className="form-group">
            <label htmlFor="specialInstructions">
              Leveringsinstruksjoner (valgfritt)
            </label>
            <textarea
              id="specialInstructions"
              name="specialInstructions"
              value={formData.specialInstructions}
              onChange={handleInputChange}
              placeholder="F.eks. ring på døren, legg pakken ved døren, osv."
              rows={3}
            />
          </div>
        </div>
      </div>

      {/* Form Validation Status */}
      <div className="form-status">
        {isFormValid() ? (
          <div className="status-valid">
            <CreditCard size={16} />
            <span>Informasjonen er komplett - klar for betaling</span>
          </div>
        ) : (
          <div className="status-incomplete">
            <span>Vennligst fyll ut alle påkrevde felt (*)</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default CustomerInfoForm;
